import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Tag } from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────
   SVG: Full Masjid Silhouette (2 minarets, main dome, side domes,
         crescent, arched windows)
───────────────────────────────────────────────────────────────── */
const MasjidSilhouette = ({ className, fill = 'currentColor' }) => (
  <svg
    viewBox="0 0 700 200"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    preserveAspectRatio="xMidYMax meet"
  >
    {/* ── Far-left thin minaret ── */}
    <rect x="8" y="70" width="16" height="130" fill={fill} />
    <polygon points="8,70 16,48 24,70" fill={fill} />
    <ellipse cx="16" cy="48" rx="9" ry="4" fill={fill} />

    {/* ── Left main minaret ── */}
    <rect x="75" y="30" width="22" height="170" fill={fill} />
    <polygon points="75,30 86,5 97,30" fill={fill} />
    <ellipse cx="86" cy="5" rx="12" ry="5.5" fill={fill} />
    {/* balcony rings */}
    <rect x="68" y="68" width="36" height="7" rx="2" fill={fill} />
    <rect x="70" y="100" width="32" height="5" rx="1.5" fill={fill} />

    {/* ── Left secondary dome ── */}
    <path d="M148,110 Q192,58 236,110" fill={fill} />
    <rect x="148" y="110" width="88" height="90" fill={fill} />
    {/* arch window */}
    <path d="M176,200 L176,176 Q192,158 208,176 L208,200" fill="rgba(0,0,0,0.28)" />

    {/* ── MAIN central dome ── */}
    <path d="M232,100 Q350,-24 468,100" fill={fill} />
    <rect x="232" y="100" width="236" height="100" fill={fill} />
    {/* grand arch door */}
    <path d="M316,200 L316,162 Q350,135 384,162 L384,200" fill="rgba(0,0,0,0.30)" />
    {/* side arch windows */}
    <path d="M252,200 L252,178 Q267,158 282,178 L282,200" fill="rgba(0,0,0,0.22)" />
    <path d="M418,200 L418,178 Q433,158 448,178 L448,200" fill="rgba(0,0,0,0.22)" />

    {/* ── Crescent on main dome ── */}
    <circle cx="350" cy="12" r="16" fill="#D4AF37" />
    <circle cx="358" cy="8" r="13" fill={fill} />
    {/* star beside crescent */}
    <g transform="translate(370, 2) scale(0.9)">
      <polygon
        points="8,0 9.8,5.5 15.5,5.5 11,9 12.7,14.5 8,11 3.3,14.5 5,9 0.5,5.5 6.2,5.5"
        fill="#D4AF37"
      />
    </g>

    {/* ── Right secondary dome ── */}
    <path d="M464,110 Q508,58 552,110" fill={fill} />
    <rect x="464" y="110" width="88" height="90" fill={fill} />
    {/* arch window */}
    <path d="M492,200 L492,176 Q508,158 524,176 L524,200" fill="rgba(0,0,0,0.28)" />

    {/* ── Right main minaret ── */}
    <rect x="603" y="30" width="22" height="170" fill={fill} />
    <polygon points="603,30 614,5 625,30" fill={fill} />
    <ellipse cx="614" cy="5" rx="12" ry="5.5" fill={fill} />
    {/* balcony rings */}
    <rect x="596" y="68" width="36" height="7" rx="2" fill={fill} />
    <rect x="598" y="100" width="32" height="5" rx="1.5" fill={fill} />

    {/* ── Far-right thin minaret ── */}
    <rect x="676" y="70" width="16" height="130" fill={fill} />
    <polygon points="676,70 684,48 692,70" fill={fill} />
    <ellipse cx="684" cy="48" rx="9" ry="4" fill={fill} />
  </svg>
);

/* ─────────────────────────────────────────────────────────────────
   SVG: Crescent + Star
───────────────────────────────────────────────────────────────── */
const CrescentStar = ({ size = 56 }) => (
  <svg width={size} height={size} viewBox="0 0 60 70" fill="none">
    <path
      d="M48,12 Q22,14 20,33 Q18,52 38,60 Q16,56 13,35 Q10,14 30,9 Q39,7 48,12"
      fill="#D4AF37"
    />
    <g transform="translate(45,5)">
      <polygon
        points="7,0 8.7,5 14,5 9.8,8 11.4,13.5 7,10.5 2.6,13.5 4.2,8 0,5 5.3,5"
        fill="#D4AF37"
      />
    </g>
  </svg>
);

/* ─────────────────────────────────────────────────────────────────
   Islamic Geometric SVG Pattern
───────────────────────────────────────────────────────────────── */
const IslamicPattern = () => (
  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="eid-geo" x="0" y="0" width="48" height="48" patternUnits="userSpaceOnUse">
        {/* diamond */}
        <path d="M24 0 L48 24 L24 48 L0 24 Z" fill="none" stroke="#D4AF37" strokeWidth="0.7" />
        {/* cross lines */}
        <path d="M24 0 L24 48 M0 24 L48 24" fill="none" stroke="#D4AF37" strokeWidth="0.35" />
        {/* inner circle */}
        <circle cx="24" cy="24" r="9" fill="none" stroke="#D4AF37" strokeWidth="0.7" />
        {/* corner dots */}
        <circle cx="0" cy="0" r="3" fill="none" stroke="#D4AF37" strokeWidth="0.6" />
        <circle cx="48" cy="0" r="3" fill="none" stroke="#D4AF37" strokeWidth="0.6" />
        <circle cx="0" cy="48" r="3" fill="none" stroke="#D4AF37" strokeWidth="0.6" />
        <circle cx="48" cy="48" r="3" fill="none" stroke="#D4AF37" strokeWidth="0.6" />
        {/* small rotated square */}
        <rect x="19" y="19" width="10" height="10" transform="rotate(45 24 24)" fill="none" stroke="#D4AF37" strokeWidth="0.6" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#eid-geo)" />
  </svg>
);

/* ─────────────────────────────────────────────────────────────────
   Twinkling Star
───────────────────────────────────────────────────────────────── */
const TwinkleStar = ({ x, y, size, delay }) => (
  <motion.div
    className="absolute pointer-events-none"
    style={{ left: `${x}%`, top: `${y}%` }}
    animate={{ opacity: [0.2, 1, 0.2], scale: [0.7, 1.3, 0.7] }}
    transition={{ duration: 1.8 + delay * 0.4, repeat: Infinity, delay, ease: 'easeInOut' }}
  >
    <svg width={size} height={size} viewBox="0 0 12 12" fill="#FFD700">
      <polygon points="6,0 7.2,4.2 12,4.2 8.4,6.8 9.7,11 6,8.5 2.3,11 3.6,6.8 0,4.2 4.8,4.2" />
    </svg>
  </motion.div>
);

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
const EidOfferBanner = ({ onClose }) => {
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(100);
  const DURATION_MS = 14000;

  // Auto-close progress bar
  useEffect(() => {
    const startTime = Date.now();
    const frame = () => {
      const elapsed = Date.now() - startTime;
      const pct = Math.max(0, 100 - (elapsed / DURATION_MS) * 100);
      setProgress(pct);
      if (pct > 0) requestAnimationFrame(frame);
      else setVisible(false);
    };
    const id = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(id);
  }, []);

  // Notify parent when hidden
  useEffect(() => {
    if (!visible) {
      const t = setTimeout(() => onClose?.(), 500);
      return () => clearTimeout(t);
    }
  }, [visible, onClose]);

  const handleClose = useCallback(() => setVisible(false), []);

  // Memoised stars
  const stars = useMemo(
    () =>
      [...Array(14)].map(() => ({
        x: Math.random() * 92 + 4,
        y: Math.random() * 55 + 3,
        size: Math.floor(Math.random() * 7 + 5),
        delay: Math.random() * 2,
      })),
    []
  );

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* ── Backdrop ───────────────────────────────────────────── */}
          <motion.div
            key="eid-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-[3px]"
            onClick={handleClose}
          />

          {/* ── Notification Card ──────────────────────────────────── */}
          <motion.div
            key="eid-card"
            initial={{ opacity: 0, y: -80, scale: 0.88 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -60, scale: 0.9 }}
            transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-0 top-6 z-[70] flex justify-center px-4"
            style={{ pointerEvents: 'none' }}
          >
            <div
              className="relative w-full max-w-3xl rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.7)]"
              style={{ pointerEvents: 'auto' }}
            >

              {/* ── Main gradient background ─────────────────────── */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'radial-gradient(ellipse at 30% 40%, #0f5a2a 0%, #063d1a 45%, #020f06 100%)',
                }}
              />

              {/* ── Islamic geometric pattern overlay ────────────── */}
              <div className="absolute inset-0 opacity-[0.07]">
                <IslamicPattern />
              </div>

              {/* ── Gold border glow ─────────────────────────────── */}
              <div
                className="absolute inset-0 rounded-3xl pointer-events-none"
                style={{
                  boxShadow: 'inset 0 0 0 1.5px rgba(212,175,55,0.55), 0 0 60px rgba(212,175,55,0.18)',
                }}
              />

              {/* ── Twinkling stars ──────────────────────────────── */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {stars.map((s, i) => (
                  <TwinkleStar key={i} {...s} />
                ))}
              </div>

              {/* ── Crescent (large, decorative, top-right) ──────── */}
              <div className="absolute top-3 right-6 opacity-90 pointer-events-none">
                <CrescentStar size={72} />
              </div>

              {/* ── Secondary crescent (small, top-left) ─────────── */}
              <div className="absolute -top-2 left-8 opacity-40 pointer-events-none scale-50">
                <CrescentStar size={50} />
              </div>

              {/* ── Gold horizontal shimmer lines ────────────────── */}
              <div
                className="absolute top-0 left-0 right-0 h-px"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, #D4AF37 30%, #FFD700 50%, #D4AF37 70%, transparent 100%)',
                }}
              />

              {/* ╔═══════════════════════════════════════════════════╗ */}
              {/*   CONTENT AREA                                       */}
              {/* ╚═══════════════════════════════════════════════════╝ */}
              <div className="relative z-10 px-6 pt-7 pb-2 md:px-10 md:pt-8">

                {/* Close button */}
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all duration-200 hover:scale-110"
                  aria-label="Close Eid offer"
                >
                  <X size={16} />
                </button>

                {/* ── Badge row ──────────────────────────────────── */}
                <div className="flex items-center justify-center gap-2 mb-3">
                  <span className="text-yellow-400 text-base">✦</span>
                  <motion.span
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="text-yellow-300 text-[11px] font-bold tracking-[0.25em] uppercase"
                  >
                    Eid Al Adha Special Offer
                  </motion.span>
                  <span className="text-yellow-400 text-base">✦</span>
                </div>

                {/* ── Arabic + English heading ───────────────────── */}
                <div className="text-center mb-5">
                  <motion.h2
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="font-bold leading-tight"
                    style={{
                      fontFamily: 'Georgia, "Times New Roman", serif',
                      fontSize: 'clamp(22px, 5vw, 36px)',
                      background: 'linear-gradient(135deg, #FFD700 0%, #F5C842 40%, #DAA520 70%, #FFD700 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      textShadow: 'none',
                      filter: 'drop-shadow(0 2px 12px rgba(212,175,55,0.5))',
                    }}
                  >
                    عيد مبارك &mdash; Eid Mubarak!
                  </motion.h2>
                  <p className="text-white/65 text-sm mt-1 tracking-wide">
                    Celebrate the blessed occasion with nutritious, chef-prepared meals
                  </p>
                </div>

                {/* ── Offer row ──────────────────────────────────── */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">

                  {/* Discount badge */}
                  <motion.div
                    animate={{ scale: [1, 1.04, 1] }}
                    transition={{ duration: 2.5, repeat: Infinity }}
                    className="relative flex-shrink-0"
                  >
                    <div
                      className="rounded-2xl px-6 py-4 text-center border"
                      style={{
                        background: 'linear-gradient(135deg, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.08) 100%)',
                        borderColor: 'rgba(212,175,55,0.5)',
                      }}
                    >
                      <p className="text-yellow-300/80 text-[10px] uppercase tracking-widest font-bold mb-0.5">
                        Limited Eid Offer
                      </p>
                      <p
                        className="font-black leading-none"
                        style={{
                          fontSize: 'clamp(36px, 8vw, 56px)',
                          background: 'linear-gradient(180deg, #FFD700 0%, #DAA520 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          filter: 'drop-shadow(0 4px 8px rgba(212,175,55,0.4))',
                        }}
                      >
                        25%
                      </p>
                      <p className="text-yellow-300/70 text-[10px] uppercase tracking-widest font-semibold mt-0.5">
                        off all meal plans
                      </p>
                    </div>
                  </motion.div>

                  {/* Divider */}
                  <div
                    className="hidden sm:block w-px h-16 self-center"
                    style={{ background: 'linear-gradient(180deg, transparent, rgba(212,175,55,0.4), transparent)' }}
                  />

                  {/* Code + CTA */}
                  <div className="flex flex-col items-center gap-3">
                    <div>
                      <p className="text-white/50 text-[11px] uppercase tracking-widest text-center mb-1.5">
                        Use promo code
                      </p>
                      <div
                        className="flex items-center gap-2.5 rounded-xl px-5 py-2.5 border"
                        style={{
                          background: 'rgba(0,0,0,0.4)',
                          borderColor: 'rgba(212,175,55,0.45)',
                        }}
                      >
                        <Tag size={14} className="text-yellow-400 flex-shrink-0" />
                        <span
                          className="font-mono font-black tracking-[0.3em] text-xl"
                          style={{
                            color: '#FFD700',
                            textShadow: '0 0 16px rgba(255,215,0,0.6)',
                          }}
                        >
                          EID25
                        </span>
                      </div>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.06 }}
                      whileTap={{ scale: 0.96 }}
                      onClick={() => (window.location.href = '/checkout')}
                      className="flex items-center gap-2 font-bold py-3 px-8 rounded-xl transition-colors duration-200"
                      style={{
                        background: 'linear-gradient(135deg, #D4AF37 0%, #F5C842 50%, #C09B2A 100%)',
                        color: '#0a3d1a',
                        boxShadow: '0 6px 24px rgba(212,175,55,0.45)',
                      }}
                    >
                      Order Now
                      <ArrowRight size={16} />
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* ── Masjid Silhouette at bottom ──────────────────── */}
              <div
                className="relative z-10 h-24 md:h-28 overflow-hidden"
                style={{ color: 'rgba(0, 30, 10, 0.55)' }}
              >
                <div className="absolute bottom-0 left-0 right-0">
                  <MasjidSilhouette className="w-full" fill="rgba(2, 22, 8, 0.6)" />
                </div>
                {/* Gold ground line */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent 0%, #D4AF37 20%, #FFD700 50%, #D4AF37 80%, transparent 100%)',
                  }}
                />
              </div>

              {/* ── Auto-close progress bar ──────────────────────── */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10">
                <motion.div
                  className="h-full"
                  style={{
                    width: `${progress}%`,
                    background:
                      'linear-gradient(90deg, #065f2e, #D4AF37)',
                    transition: 'width 0.05s linear',
                  }}
                />
              </div>

            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EidOfferBanner;
